import { createContext, useEffect, useState } from "react";
import { getParsedNftAccountsByOwner } from "@nfteyez/sol-rayz";
import { useWallet } from "@solana/wallet-adapter-react";
import { Connection } from "@solana/web3.js";
import { getAllNFTs } from "../contexts/scripts";
import { SOLANA_RPC } from "../config";
import {
  getAllListedNfts,
  getActivityBySeller,
  getAllCollection,
  getActivityAll,
  getAllAuctionBySeller,
  getAllOfferBySeller,
} from "../utils/solanaAPI";
export const GetSolNftDataContext = createContext({
  nfts: [],
  listedNfts: [],
  activityData: [],
  auctionData: [],
  collectionData: [],
  listedNftLoading: false,
  loading: false,
  getInfo: () => {},
});

const GetSolNftProvider = ({ children }) => {
  const solConnection = new Connection(SOLANA_RPC);
  const { publicKey } = useWallet();
  const [nfts, setNfts] = useState([]);
  const [listedNfts, setListedNfts] = useState([]);
  const [auctionData, setAuctions] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [collectionData, setCollectionData] = useState([]);
  const [listedNftLoading, setListedNftLoading] = useState(true);
  const [error, setError] = useState(null);
  const [listError, setListError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchNfts = async () => {
    setLoading(true);
    try {
      // Fetch the NFT accounts for the given address
      const nftAccounts = await getParsedNftAccountsByOwner({
        publicAddress: publicKey ?? "",
        connection: solConnection,
      });

      // If there are no NFT accounts, return an empty array
      if (!nftAccounts || nftAccounts.length === 0) {
        setLoading(false);
        return;
      }

      // Prepare an array to hold the full NFT data
      let fullData = [];

      // Fetch metadata for each NFT concurrently
      await Promise.all(
        nftAccounts.map(async (account) => {
          // Check if the NFT is verified and the creator address matches the specified address
          if (
            account.data.creators !== undefined &&
            account.data.creators.find((creator) => creator.verified === 1)
          ) {
            // Fetch metadata from the URI
            const metadataRes = await fetch(account.data.uri);
            if (!metadataRes.ok) {
              throw new Error("Failed to fetch metadata from uri");
            }

            // Parse the metadata JSON
            const metadata = await metadataRes.json();
            // Construct the NFT object
            const nft = {
              tokenId: account.data.name.split("#")[1],
              collectionAddr: account.data.creators[0].address,
              name: account.data.name,
              mintAddr: account.mint,
              imgUrl: metadata.image,
              metaDataUrl: account.data.uri,
              updateAuthority: account.updateAuthority,
              data: account.data,
              primarySaleHappened: account.primarySaleHappened,
              isMutable: account.isMutable,
              editionNonce: account.editionNonce,
              masterEdition: account.masterEdition,
              edition: account.edition,
              seller: publicKey.toBase58(),
              mode: "unlisted",
            };

            // Add the NFT to the full data array
            nft.tokenId && fullData.push(nft);
          }
        })
      );

      setNfts(fullData);
      setLoading(false);
    } catch (error) {
      // Log any errors that occur during the process
      console.log(error);
      setLoading(false);
      setError(error);
      return;
    }
  };

  // const getListedNfts = async () => {
  //   setListedNftLoading(true);
  //   try {
  //     const listedNfts = await getAllNFTs();

  //     // If there are no NFT accounts, return an empty array
  //     if (!listedNfts || listedNfts.length === 0) {
  //       setListedNftLoading(false);
  //       return;
  //     }

  //     let fullData = [];

  //     await Promise.all(
  //       listedNfts.data.map(async (account) => {
  //         const nft = {
  //           active: account.active,
  //           collection: account.collection,
  //           listedDate: account.listedDate,
  //           mint: account.mint,
  //           priceSol: account.priceSol,
  //           seller: account.seller,
  //           mode: "listed",
  //         };

  //         // Add the NFT to the full data array
  //         fullData.push(nft);
  //       })
  //     );

  //     setListedNfts(fullData);
  //     setListedNftLoading(false);
  //     return fullData;
  //   } catch (error) {
  //     console.log(error);
  //     setListedNftLoading(false);
  //     setListError(error);
  //     return;
  //   }
  // };

  const getListedNftsFromDB = async () => {
    try {
      setListedNftLoading(true);
      const data = await getAllListedNfts();
      setListedNfts(data);
      setListedNftLoading(false);
    } catch (error) {
      console.log("err", error);
      setListedNftLoading(false);
    }
  };

  const getAuctionBySeller = async (seller) => {
    try {
      const data = await getAllAuctionBySeller(seller);
      setAuctions(data);
    } catch (error) {
      console.log("err", error);
    }
  };

  const getActivityData = async () => {
    try {
      const data = await getActivityAll();
      setActivityData(data);
    } catch (error) {
      console.log("err", error);
    }
  };

  const getAllCollectionData = async () => {
    try {
      const data = await getAllCollection();
      setCollectionData(data);
    } catch (error) {
      console.log("err", error);
    }
  };

  const getInfo = async () => {
    await Promise.all([
      fetchNfts(),
      getListedNftsFromDB(),
      getActivityData(),
      getAllCollectionData(),
    ]);
  };

  useEffect(() => {
    if (publicKey) {
      getAuctionBySeller(publicKey.toBase58());
      const interval = setInterval(() => {
        getAuctionBySeller(publicKey.toBase58());
      }, 65000); // 1 minute
      return () => clearInterval(interval);
    } else {
      setAuctions([]);
    }
  }, [publicKey]);

  useEffect(() => {
    if (publicKey) {
      getInfo();
    } else {
      setNfts([]);
      setListedNfts([]);
    }
  }, [publicKey]);

  return (
    <GetSolNftDataContext.Provider
      value={{
        nfts,
        listedNfts,
        activityData,
        auctionData,
        collectionData,
        listedNftLoading,
        loading,
        getInfo,
      }}
    >
      {children}
    </GetSolNftDataContext.Provider>
  );
};

export default GetSolNftProvider;
